import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import AdminLogin from "./AdminLogin";
import { api } from "../lib/api";
import Header from "../components/Header";
import { toast } from "sonner";
import {
  LogOut, Settings, CalendarDays, Truck, Users, BarChart3, ShieldCheck, Menu, X,
  FileText, UserCheck, DollarSign, Route, Bell, ScrollText, History, Star, CalendarClock, Handshake, Package, Images,
} from "lucide-react";

export const canAdmin = (user, key) =>
  user?.admin_nivel === "super" || (user?.admin_permissoes || []).includes(key);

// Buzina de caminhão sintetizada (Web Audio API) com volume configurável
export function playHorn(volume = 0.8) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const master = ctx.createGain();
    master.gain.value = Math.max(0, Math.min(1, volume));
    master.connect(ctx.destination);
    const mk = (freq, start, dur) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(master);
      o.type = "sawtooth";
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(0.5, ctx.currentTime + start + 0.04);
      g.gain.setValueAtTime(0.5, ctx.currentTime + start + dur - 0.08);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
      o.start(ctx.currentTime + start);
      o.stop(ctx.currentTime + start + dur);
    };
    mk(233, 0, 0.5);   // Bb3
    mk(175, 0, 0.5);   // F3 (harmônico da buzina)
    mk(196, 0.55, 0.8); // G3 — segundo toque
    mk(147, 0.55, 0.8);
  } catch {}
}

export function getSoundPrefs() {
  try {
    const raw = localStorage.getItem("cjr_adm_sound");
    if (raw) return { ativo: true, volume: 0.8, ...JSON.parse(raw) };
  } catch {}
  return { ativo: true, volume: 0.8 };
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

export function useAdmin() {
  const location = useLocation();
  const [state, setState] = useState(
    location.state?.user
      ? { status: location.state.user.is_admin ? "ok" : "denied", user: location.state.user }
      : { status: "loading", user: null }
  );

  useEffect(() => {
    if (location.state?.user) return;
    api
      .get("/auth/me")
      .then((r) => setState(r.data.is_admin ? { status: "ok", user: r.data } : { status: "denied", user: r.data }))
      .catch(() => setState({ status: "anon", user: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}

const MENU = [
  { to: "/admin/dashboard", label: "Dashboard", icon: BarChart3, perm: "dashboard" },
  { to: "/admin", label: "Solicitações", icon: FileText, perm: "orcamentos", end: true, cat: "orcamentos" },
  { to: "/admin/corridas", label: "Corridas", icon: Truck, perm: "corridas", cat: "corridas" },
  { to: "/admin/agenda", label: "Agenda e Vagas", icon: CalendarClock, perm: "corridas" },
  { to: "/admin/reservas", label: "Reservas", icon: CalendarDays, perm: "reservas", cat: "pagamentos" },
  { to: "/admin/motoristas", label: "Motoristas / Parceiros", icon: Users, perm: "motoristas", cat: "motoristas" },
  { to: "/admin/motoristas?f=aprovacao", label: "Aprovação de motoristas", icon: UserCheck, perm: "motoristas" },
  { to: "/admin/indicadores", label: "Indicadores", icon: Handshake, perm: "indicadores", cat: "indicadores" },
  { to: "/admin/divulgacao", label: "Imagens de Divulgação", icon: Images, perm: "indicadores" },
  { to: "/admin/clientes", label: "Clientes", icon: Users, perm: "orcamentos" },
  { to: "/admin/precos", label: "Preços e Regras", icon: DollarSign, perm: "precos" },
  { to: "/admin/servicos", label: "Gerenciar Serviços", icon: Package, perm: "precos" },
  { to: "/admin/config", label: "Pedágios", icon: Route, perm: "configuracoes" },
  { to: "/admin/config", label: "Configurações", icon: Settings, perm: "configuracoes" },
  { to: "/admin/admins", label: "Administradores", icon: ShieldCheck, superOnly: true },
  { to: "/admin/notificacoes", label: "Notificações e Alertas", icon: Bell, always: true, cat: "total" },
  { to: "/admin/termos", label: "Termos e Documentos", icon: ScrollText, perm: "termos" },
  { to: "/admin/avaliacoes", label: "Avaliações", icon: Star, perm: "motoristas" },
  { to: "/admin/logs", label: "Histórico / Logs", icon: History, perm: "logs" },
];

export function AdminShell({ children, user, title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [porCat, setPorCat] = useState({});
  const [latest, setLatest] = useState([]);
  const [bellOpen, setBellOpen] = useState(false);
  const prevUnread = useRef(null);
  const eventosRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [r, c] = await Promise.all([
          api.get("/admin/notificacoes"),
          api.get("/admin/notificacoes/config").catch(() => ({ data: null })),
        ]);
        if (c.data?.alerta_adm?.eventos) eventosRef.current = c.data.alerta_adm.eventos;
        const n = r.data.unread || 0;
        setLatest(r.data.notificacoes?.slice(0, 6) || []);
        if (prevUnread.current !== null && n > prevUnread.current) {
          const nova = (r.data.notificacoes || []).find((x) => !x.lida);
          const prefs = getSoundPrefs();
          const tipo = nova?.tipo || "info";
          const habilitado = eventosRef.current ? eventosRef.current[tipo] !== false : true;
          if (prefs.ativo && habilitado) playHorn(prefs.volume);
          toast.info(nova?.texto || "Nova notificação", { duration: 8000 });
        }
        prevUnread.current = n;
        setUnread(n);
        setPorCat(r.data.unread_categorias || {});
      } catch {}
    };
    load();
    const i = setInterval(load, 20000);
    return () => clearInterval(i);
  }, []);

  // Web Push: registra service worker e inscreve o admin
  useEffect(() => {
    (async () => {
      try {
        if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
        const reg = await navigator.serviceWorker.register("/sw.js");
        if (Notification.permission === "default") await Notification.requestPermission();
        if (Notification.permission !== "granted") return;
        const { data } = await api.get("/push/vapid-public");
        if (!data.publicKey) return;
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
        await api.post("/push/subscribe", { subscription: sub.toJSON() });
      } catch {}
    })();
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      navigate("/admin", { replace: true, state: {} });
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-[#0b1220] flex justify-center">
      <div className="w-full max-w-md mx-auto min-h-screen bg-slate-50 shadow-2xl relative pb-10">
        <Header
          right={
            <div className="flex items-center gap-1">
              <div className="relative">
                <button data-testid="admin-bell-btn" onClick={() => setBellOpen((v) => !v)}
                  className="p-2 text-navy hover:bg-slate-100 rounded-lg relative">
                  <Bell className="w-5 h-5" />
                  {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center" data-testid="admin-bell-badge">
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                </button>
                {bellOpen && (
                  <div className="absolute right-0 top-11 w-80 max-w-[85vw] bg-white rounded-2xl shadow-2xl border border-slate-100 z-50 overflow-hidden" data-testid="admin-bell-dropdown">
                    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                      <p className="text-xs font-extrabold text-navy uppercase tracking-wide">Notificações</p>
                      <Link to="/admin/notificacoes" onClick={() => setBellOpen(false)} className="text-[11px] font-bold text-[#F5B301]" data-testid="bell-ver-todas">
                        Ver todas
                      </Link>
                    </div>
                    {latest.length === 0 && <p className="text-xs text-slate-400 p-4">Nenhuma notificação.</p>}
                    {latest.map((n) => (
                      <Link key={n.id} to={n.link || "/admin/notificacoes"} onClick={() => setBellOpen(false)}
                        className={`block px-4 py-2.5 border-b border-slate-50 last:border-0 text-xs ${n.lida ? "text-slate-500" : "text-navy font-semibold bg-gold-light/40"}`}
                        data-testid={`bell-notif-${n.id}`}>
                        {n.texto}
                        <span className="block text-[9px] text-slate-400 mt-0.5">{new Date(n.em).toLocaleString("pt-BR")}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <button data-testid="admin-menu-btn" onClick={() => setMenuOpen(true)} className="p-2 text-navy hover:bg-slate-100 rounded-lg">
                <Menu className="w-5 h-5" />
              </button>
              <button data-testid="admin-logout-btn" onClick={logout} className="p-2 text-slate-400 hover:text-red-500 rounded-lg">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          }
        />

        {menuOpen && (
          <div className="fixed inset-0 z-50" data-testid="admin-drawer">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMenuOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-navy text-white shadow-2xl overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <img src="/logo.jpg" alt="Carlos Jr" className="h-10 w-auto rounded-lg bg-white p-1" />
                <button data-testid="admin-drawer-close" onClick={() => setMenuOpen(false)} className="p-2 text-slate-300">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="p-2">
                {MENU.filter((m) => m.always || (m.superOnly ? user?.admin_nivel === "super" : canAdmin(user, m.perm))).map(({ to, label, icon: Icon, end, cat }) => {
                  const active = end ? location.pathname === to : location.pathname === to.split("?")[0] && !end;
                  const catCount = cat === "total" ? unread : cat ? porCat[cat] || 0 : 0;
                  return (
                    <Link key={label} to={to} onClick={() => setMenuOpen(false)} data-testid={`menu-${label.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                      className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold mb-0.5 transition-colors ${
                        active ? "bg-[#F5B301] text-navy" : "text-slate-200 hover:bg-white/10"}`}>
                      <Icon className="w-4.5 h-4.5 w-[18px] h-[18px]" /> <span className="flex-1">{label}</span>
                      {catCount > 0 && (
                        <span data-testid={`menu-badge-${cat}`} className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center">
                          {catCount > 99 ? "99+" : catCount}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </nav>
              <p className="px-4 py-3 text-[10px] text-slate-500 border-t border-white/10">
                {user?.email} · {user?.admin_nivel === "super" ? "SUPER ADM" : "ADM"}
              </p>
            </div>
          </div>
        )}

        <div className="px-4 pt-4">
          <h1 className="font-heading text-xl font-bold text-navy" data-testid="admin-title">{title}</h1>
          {user && <p className="text-xs text-slate-400">{user.email}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}

export function AdminGate({ children }) {
  const auth = useAdmin();

  if (auth.status === "loading") {
    return (
      <div className="min-h-screen bg-[#0b1220] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#F5B301] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (auth.status !== "ok") {
    if (auth.status !== "denied") {
      return <AdminLogin />;
    }
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-6">
        <div className="w-full max-w-sm text-center">
          <img src="/logo.jpg" alt="Carlos Jr Fretes e Mudanças" className="h-24 w-auto object-contain mx-auto mb-6 rounded-xl bg-white p-1.5" />
          <h1 className="font-heading text-2xl font-bold text-white">Painel Administrativo</h1>
          <p className="text-red-300 text-sm mt-3" data-testid="admin-denied-msg">
            A conta {auth.user?.email} não tem permissão de administrador.
          </p>
          <Link to="/painel-gestao" className="block mt-4 text-xs text-[#F5B301] underline" data-testid="admin-trocar-conta-link">
            Entrar com outra conta
          </Link>
          <Link to="/" className="block mt-2 text-xs text-slate-400 underline" data-testid="back-home-link">
            Voltar ao início
          </Link>
        </div>
      </div>
    );
  }

  return children(auth.user);
}
